<template>
    <div class="border border-gray-200 rounded-lg bg-white overflow-hidden">
        <div class="p-4">
            <div class="flex items-start justify-between gap-3">
                <div class="min-w-0 flex-1">
                    <div class="flex flex-wrap items-center gap-2 mb-1">
                        <span class="font-semibold text-gray-800 text-sm">{{ serie.title }}</span>
                        <span v-if="serie.execution_mode === 'reminder'" class="text-xs px-2 py-0.5 rounded-full font-medium bg-purple-100 text-purple-700">
                            Promemoria
                        </span>
                        <span :class="['text-xs px-2 py-0.5 rounded-full font-medium', seriesStatusClass(serie.status)]">
                            {{ seriesStatusLabel(serie.status) }}
                        </span>
                    </div>
                    <p v-if="serie.description" class="text-xs text-gray-500 mb-2 line-clamp-2">{{ serie.description }}</p>
                    <div class="flex flex-wrap gap-3 text-xs text-gray-600">
                        <span><span class="text-gray-400">Periodicità: </span>{{ periodicityLabel(serie) }}</span>
                        <span v-if="serie.department"><span class="text-gray-400">Reparto: </span>{{ serie.department.name }}</span>
                        <span v-if="serie.assigned_users && serie.assigned_users.length">
                            <span class="text-gray-400">Assegnatari: </span>{{ serie.assigned_users.map(u => u.name).join(', ') }}
                        </span>
                        <span><span class="text-gray-400">Occorrenze: </span>{{ serie.dates_count ?? 0 }}</span>
                        <span><span class="text-gray-400">Attività: </span>{{ serie.activities_count ?? 0 }}</span>
                        <span v-if="serie.next_date"><span class="text-gray-400">Prossima: </span>{{ formatDate(serie.next_date) }}</span>
                    </div>
                </div>
                <button @click="$emit('toggle-expand')"
                    class="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors flex-shrink-0"
                    :title="expanded ? 'Comprimi occorrenze' : 'Espandi occorrenze'">
                    <svg class="w-4 h-4 transition-transform" :class="{ 'rotate-180': expanded }" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
                    </svg>
                </button>
            </div>

            <div v-if="canManage" class="flex flex-wrap gap-2 mt-3 pt-3 border-t border-gray-100">
                <button @click="$emit('edit')" class="text-xs px-2.5 py-1 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded transition-colors">Modifica</button>
                <button @click="$emit('add-date')" class="text-xs px-2.5 py-1 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded transition-colors">Aggiungi data</button>
                <button v-if="serie.status === 'active'" @click="$emit('suspend')" class="text-xs px-2.5 py-1 bg-amber-100 hover:bg-amber-200 text-amber-700 rounded transition-colors">Sospendi</button>
                <button v-else-if="serie.status === 'suspended'" @click="$emit('resume')" class="text-xs px-2.5 py-1 bg-green-100 hover:bg-green-200 text-green-700 rounded transition-colors">Riattiva</button>
                <button @click="$emit('regenerate')" class="text-xs px-2.5 py-1 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded transition-colors">Rigenera future</button>
                <button @click="$emit('delete')" class="text-xs px-2.5 py-1 bg-red-100 hover:bg-red-200 text-red-700 rounded transition-colors">Elimina</button>
            </div>
        </div>

        <div v-if="expanded" class="border-t border-gray-200 bg-gray-50">
            <div v-if="!serie.dates || serie.dates.length === 0" class="px-4 py-3 text-xs text-gray-400 italic">
                Nessuna occorrenza.
            </div>
            <div v-else class="divide-y divide-gray-100">
                <div v-for="date in serie.dates" :key="date.id" class="px-4 py-2.5 flex items-start justify-between gap-3">
                    <div class="flex-1 min-w-0">
                        <div class="flex flex-wrap items-center gap-2 mb-1">
                            <span class="text-xs font-medium text-gray-700">{{ formatDate(date.scheduled_date) }}</span>
                            <span :class="['text-xs px-1.5 py-0.5 rounded font-medium', occurrenceStatusClass(date.status)]">
                                {{ occurrenceStatusLabel(date.status) }}
                            </span>
                            <span v-if="date.is_manual" class="text-xs px-1.5 py-0.5 rounded bg-purple-100 text-purple-700">Manuale</span>
                            <span v-if="date.is_override" class="text-xs px-1.5 py-0.5 rounded bg-orange-100 text-orange-700">Override</span>
                        </div>
                        <div v-if="date.activity" class="flex flex-wrap items-center gap-1.5 text-xs">
                            <span class="text-gray-400">Attività:</span>
                            <span class="text-gray-700 font-medium">{{ date.activity.title }}</span>
                            <span :class="['px-1.5 py-0.5 rounded font-medium', activityStatusClass(date.activity.status)]">
                                {{ activityStatusLabel(date.activity.status) }}
                            </span>
                            <span v-if="date.activity.owners && date.activity.owners.length" class="text-gray-500">
                                · {{ date.activity.owners.map(u => u.name).join(', ') }}
                            </span>
                        </div>
                        <div v-else class="text-xs text-gray-400 italic">Attività non ancora generata</div>
                    </div>
                    <div v-if="canManage && canDeleteDate(date)" class="flex-shrink-0">
                        <button @click="$emit('delete-date', date)" class="p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors" title="Elimina occorrenza">
                            <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                            </svg>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'SeriesCard',
    props: ['serie', 'canManage', 'expanded'],
    emits: ['toggle-expand', 'edit', 'add-date', 'suspend', 'resume', 'regenerate', 'delete', 'delete-date'],
    methods: {
        seriesStatusClass(status) {
            const map = {
                active: 'bg-green-100 text-green-700',
                suspended: 'bg-amber-100 text-amber-700',
                completed: 'bg-blue-100 text-blue-700',
                cancelled: 'bg-gray-100 text-gray-500',
            };
            return map[status] || 'bg-gray-100 text-gray-600';
        },
        seriesStatusLabel(status) {
            const map = { active: 'Attiva', suspended: 'Sospesa', completed: 'Completata', cancelled: 'Annullata' };
            return map[status] || status || '-';
        },
        occurrenceStatusClass(status) {
            const map = {
                planned: 'bg-gray-100 text-gray-600',
                generated: 'bg-blue-100 text-blue-700',
                skipped: 'bg-amber-100 text-amber-700',
                cancelled: 'bg-red-100 text-red-600',
            };
            return map[status] || 'bg-gray-100 text-gray-600';
        },
        occurrenceStatusLabel(status) {
            const map = { planned: 'Pianificata', generated: 'Generata', skipped: 'Saltata', cancelled: 'Annullata' };
            return map[status] || status || '-';
        },
        activityStatusClass(status) {
            const map = {
                nuova: 'bg-gray-100 text-gray-700',
                assegnata: 'bg-blue-100 text-blue-700',
                schedulata: 'bg-purple-100 text-purple-700',
                in_lavorazione: 'bg-orange-100 text-orange-700',
                completata: 'bg-green-100 text-green-700',
            };
            return map[status] || 'bg-gray-100 text-gray-700';
        },
        activityStatusLabel(status) {
            const map = {
                nuova: 'Nuova', assegnata: 'Assegnata', schedulata: 'Schedulata',
                in_lavorazione: 'In lavorazione', completata: 'Completata',
            };
            return map[status] || status || '-';
        },
        canDeleteDate(date) {
            return !['skipped', 'cancelled'].includes(date.status)
                && (!date.activity || !['in_lavorazione', 'completata'].includes(date.activity.status));
        },
        parseLocalDate(value) {
            if (!value) return null;
            if (value instanceof Date) return new Date(value.getTime());
            const normalized = String(value).substring(0, 10);
            const match = normalized.match(/^(\d{4})-(\d{2})-(\d{2})$/);
            if (match) {
                const year = Number(match[1]);
                const month = Number(match[2]) - 1;
                const day = Number(match[3]);
                const date = new Date(year, month, day);
                if (date.getFullYear() !== year || date.getMonth() !== month || date.getDate() !== day) {
                    return null;
                }
                return date;
            }
            const fallback = new Date(value);
            return Number.isNaN(fallback.getTime()) ? null : fallback;
        },
        formatDate(value) {
            if (!value) return '-';
            const date = this.parseLocalDate(value);
            if (!date || Number.isNaN(date.getTime())) return value;
            return new Intl.DateTimeFormat('it-IT', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
            }).format(date);
        },
        periodicityLabel(serie) {
            if (serie.recurrence_mode === 'manual') return 'Date manuali';
            const interval = Number(serie.recurrence_interval || 1);
            const singular = { day: 'giorno', week: 'settimana', month: 'mese', year: 'anno' };
            const plural   = { day: 'giorni',  week: 'settimane', month: 'mesi',  year: 'anni'  };
            const unit = interval === 1 ? singular[serie.recurrence_unit] : plural[serie.recurrence_unit];
            let label = interval === 1
                ? `Ogni ${unit || serie.recurrence_unit}`
                : `Ogni ${interval} ${unit || serie.recurrence_unit}`;
            if (serie.end_type === 'count' && serie.occurrences_count) {
                label += ` · ${serie.occurrences_count} occorrenze`;
            } else if (serie.ends_on) {
                label += ` · fino al ${this.formatDate(serie.ends_on)}`;
            }
            return label;
        },
    },
};
</script>
